import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./index.css";
import { ROUTES, getRouteFromScreen, getScreenFromRoute } from "./helpers/navigation";
import Home from "./components/Home";
import Info from "./components/Info";
import IdeaValidator from "./components/IdeaValidator";
import MarketInsights from "./components/MarketInsights";
import PitchDeckAnalyzer from "./components/PitchDeckAnalyzer";
import { darkModeColors, lightModeColors } from "./theme/colors";
import { logAnalyticsEvent } from "./firebase";
import { strings } from "./translations/strings";
import { api_endpoint } from "./config/environment";
import {
  uploadFile,
  createRun,
  createThread,
  checkStatus,
  getResponse,
} from "./components/PitchDeckAnalyzer/utils";

const SCREENS = {
  HOME: "Home",
  INFO: "Info",
  IDEA_VALIDATOR: "IdeaValidator",
  MARKET_INSIGHTS: "MarketInsights",
  PITCH_DECK_ANALYZER: "PitchDeckAnalyzer",
  HEADER: "Header",
  SIDEBAR: "Sidebar",
  FOOTER: "Footer",
};

const COMPONENTS = {
  [SCREENS.HOME]: Home,
  [SCREENS.INFO]: Info,
  [SCREENS.IDEA_VALIDATOR]: IdeaValidator,
  [SCREENS.MARKET_INSIGHTS]: MarketInsights,
  [SCREENS.PITCH_DECK_ANALYZER]: PitchDeckAnalyzer,
};

const EVENT_LOGS = {
  [SCREENS.IDEA_VALIDATOR]: "idea_validator_opened",
  [SCREENS.MARKET_INSIGHTS]: "market_insights_opened",
  [SCREENS.PITCH_DECK_ANALYZER]: "pitch_deck_analyzer_opened",
};

export const useAppLogic = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(SCREENS.HOME);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMenuOpened, setIsMenuOpened] = useState(false);

  const [aIResponseIdeaValidator, setAIResponseIdeaValidator] = useState("");
  const [isLoadingIdeaValidator, setIsLoadingIdeaValidator] = useState(false);
  const [isErrorIdeaValidator, setIsErrorIdeaValidator] = useState(false);
  const [userInputIdeaValidator, setUserInputIdeaValidator] = useState("");
  const [isPressedFeedbackIdeaValidator, setIsPressedFeedbackIdeaValidator] =
    useState(false);

  const [aIResponseMarketInsights, setAIResponseMarketInsights] = useState("");
  const [isLoadingMarketInsights, setIsLoadingMarketInsights] = useState(false);
  const [isErrorMarketInsights, setIsErrorMarketInsights] = useState(false);
  const [userInputMarketInsights, setUserInputMarketInsights] = useState("");
  const [isPressedFeedbackMarketInsights, setIsPressedFeedbackMarketInsights] =
    useState(false);

  const [aIResponsePitchDeckAnalyzer, setAIResponsePitchDeckAnalyzer] =
    useState("");
  const [isLoadingPitchDeckAnalyzer, setIsLoadingPitchDeckAnalyzer] =
    useState(false);
  const [isErrorPitchDeckAnalyzer, setIsErrorPitchDeckAnalyzer] =
    useState(false);
  const [userFilePitchDeckAnalyzer, setUserFilePitchDeckAnalyzer] =
    useState(null);
  const [
    isPressedFeedbackPitchDeckAnalyzer,
    setIsPressedFeedbackPitchDeckAnalyzer,
  ] = useState(false);

  const handleInputChangeIdeaValidator = (text) => {
    setUserInputIdeaValidator(text);
  };
  const handleInputChangeMarketInsights = (text) => {
    setUserInputMarketInsights(text);
  };

  const setErrorIdeaValidator = () => {
    setIsErrorIdeaValidator(true);
    setAIResponseIdeaValidator(strings.no_response_text);
  };
  const setErrorMarketInsights = () => {
    setIsErrorMarketInsights(true);
    setAIResponseMarketInsights(strings.no_response_text);
  };

  const onPressButtonMarketInsights = () => {
    if (userInputMarketInsights?.length) {
      setIsLoadingMarketInsights(true);
      logAnalyticsEvent("market_insights_triggered");
      const endpoint = `${api_endpoint}ai/create`;
      const formData = new FormData();
      formData.append("ideaDescription", userInputMarketInsights);
      formData.append("type", strings.market_insights_type);
      const requestOptions = {
        method: "POST",
        body: formData,
      };
      fetch(endpoint, requestOptions)
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else if (response?.status === 429) {
            throw new Error(strings.rate_limit_exceeded);
          } else {
            setErrorMarketInsights();
          }
        })
        .then((data) => {
          if (data) {
            setAIResponseMarketInsights(data?.content);
          } else {
            setErrorMarketInsights();
          }
        })
        .catch((error) => {
          setIsErrorMarketInsights(true);
          if (error.message === strings.rate_limit_exceeded) {
            setAIResponseMarketInsights(strings.rate_limit_error);
          } else {
            setAIResponseMarketInsights(strings.no_response_text);
          }
        })
        .finally(() => {
          setIsLoadingMarketInsights(false);
        });
    }
  };
  const onPressButtonIdeaValidator = () => {
    if (userInputIdeaValidator?.length) {
      setIsLoadingIdeaValidator(true);
      logAnalyticsEvent("idea_validator_triggered");
      const endpoint = `${api_endpoint}ai/create`;
      const formData = new FormData();
      formData.append("ideaDescription", userInputIdeaValidator);
      formData.append("type", strings.idea_validator_type);
      const requestOptions = {
        method: "POST",
        body: formData,
      };
      fetch(endpoint, requestOptions)
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else if (response?.status === 429) {
            throw new Error(strings.rate_limit_exceeded);
          } else {
            setErrorIdeaValidator();
          }
        })
        .then((data) => {
          if (data) {
            setAIResponseIdeaValidator(data?.content);
          } else {
            setErrorIdeaValidator();
          }
        })
        .catch((error) => {
          setIsErrorIdeaValidator(true);
          if (error.message === strings.rate_limit_exceeded) {
            setAIResponseIdeaValidator(strings.rate_limit_error);
          } else {
            setAIResponseIdeaValidator(strings.no_response_text);
          }
        })
        .finally(() => {
          setIsLoadingIdeaValidator(false);
        });
    }
  };
  const onPressButtonPitchDeckAnalyzer = async () => {
    if (userFilePitchDeckAnalyzer) {
      setIsLoadingPitchDeckAnalyzer(true);
      logAnalyticsEvent("pitch_deck_analyzer_triggered");
      try {
        const fileId = await uploadFile(userFilePitchDeckAnalyzer);
        const threadId = await createThread(fileId);
        const { runId } = await createRun(threadId);
        await checkStatus({ threadId, runId });
        const response = await getResponse(threadId);
        setAIResponsePitchDeckAnalyzer(response?.content);
        setIsLoadingPitchDeckAnalyzer(false);
      } catch (error) {
        setIsErrorPitchDeckAnalyzer(true);
        if (error.message === strings.rate_limit_exceeded) {
          setAIResponsePitchDeckAnalyzer(strings.rate_limit_error);
        } else {
          setAIResponsePitchDeckAnalyzer(strings.no_response_text);
        }
        setIsLoadingPitchDeckAnalyzer(false);
      }
    }
  };
  const resetIdeaValidator = () => {
    setIsErrorIdeaValidator(false);
    setAIResponseIdeaValidator("");
    setIsLoadingIdeaValidator(false);
    setUserInputIdeaValidator("");
    setIsPressedFeedbackIdeaValidator(false);
  };
  const resetMarketInsights = () => {
    setIsErrorMarketInsights(false);
    setAIResponseMarketInsights("");
    setIsLoadingMarketInsights(false);
    setUserInputMarketInsights("");
    setIsPressedFeedbackMarketInsights(false);
  };
  const resetPitchDeckAnalyzer = () => {
    setIsErrorPitchDeckAnalyzer(false);
    setAIResponsePitchDeckAnalyzer("");
    setIsLoadingPitchDeckAnalyzer(false);
    setUserFilePitchDeckAnalyzer(null);
    setIsPressedFeedbackPitchDeckAnalyzer(false);
  };

  const colors = isDarkMode ? darkModeColors : lightModeColors;
  const goBack = () => {
    changeScreen(SCREENS.HOME);
  };

  const navigateToRoute = (route) => {
    if (Object.values(ROUTES).includes(route)) {
      navigate(route);
      const screen = getScreenFromRoute(route);
      setCurrentScreen(SCREENS[screen]);
    }
  };

  const changeScreen = (screen) => {
    setCurrentScreen(screen);
    const eventLog = EVENT_LOGS?.[screen];
    if (eventLog) {
      logAnalyticsEvent(eventLog);
    }

    // Navigate to corresponding route
    const route = getRouteFromScreen(screen);
    navigate(route);
  };
  const toggleDarkMode = () => {
    setIsDarkMode((prevMode) => !prevMode);
  };
  const onPressMenu = () => {
    setIsMenuOpened((prevMode) => !prevMode);
  };
  const onPressSidebarItem = (screen) => {
    changeScreen(screen);
    setIsMenuOpened(false);
  };

  const COMPONENT_PROPS = {
    [SCREENS.IDEA_VALIDATOR]: {
      userInput: userInputIdeaValidator,
      handleInputChange: handleInputChangeIdeaValidator,
      aIResponse: aIResponseIdeaValidator,
      isError: isErrorIdeaValidator,
      isLoading: isLoadingIdeaValidator,
      onPressButton: onPressButtonIdeaValidator,
      onPressReset: resetIdeaValidator,
      colors,
      goBack,
      isDarkMode,
      isPressedFeedback: isPressedFeedbackIdeaValidator,
      setIsPressedFeedback: setIsPressedFeedbackIdeaValidator,
    },
    [SCREENS.MARKET_INSIGHTS]: {
      userInput: userInputMarketInsights,
      handleInputChange: handleInputChangeMarketInsights,
      aIResponse: aIResponseMarketInsights,
      isError: isErrorMarketInsights,
      isLoading: isLoadingMarketInsights,
      onPressButton: onPressButtonMarketInsights,
      onPressReset: resetMarketInsights,
      colors,
      goBack,
      isDarkMode,
      isPressedFeedback: isPressedFeedbackMarketInsights,
      setIsPressedFeedback: setIsPressedFeedbackMarketInsights,
    },
    [SCREENS.PITCH_DECK_ANALYZER]: {
      userFile: userFilePitchDeckAnalyzer,
      setUserFile: setUserFilePitchDeckAnalyzer,
      aIResponse: aIResponsePitchDeckAnalyzer,
      isError: isErrorPitchDeckAnalyzer,
      isLoading: isLoadingPitchDeckAnalyzer,
      onPressButton: onPressButtonPitchDeckAnalyzer,
      onPressReset: resetPitchDeckAnalyzer,
      colors,
      goBack,
      isDarkMode,
      isPressedFeedback: isPressedFeedbackPitchDeckAnalyzer,
      setIsPressedFeedback: setIsPressedFeedbackPitchDeckAnalyzer,
    },
    [SCREENS.HOME]: {
      colors,
      changeScreen,
      SCREENS,
      isDarkMode,
    },
    [SCREENS.INFO]: {
      colors,
      isDarkMode,
    },
    [SCREENS.HEADER]: {
      changeScreen,
      SCREENS,
      toggleDarkMode,
      colors,
      onPressMenu,
      isMenuOpened,
      isDarkMode,
    },
    [SCREENS.SIDEBAR]: {
      SCREENS,
      colors,
      isMenuOpened,
      currentScreen,
      onPressSidebarItem,
    },
    [SCREENS.FOOTER]: {
      colors,
      isDarkMode,
    },
  };
  useEffect(() => {
    logAnalyticsEvent("app_opened");
  }, []);

  // Sync currentScreen with current route
  useEffect(() => {
    const path = location.pathname;
    const screen = getScreenFromRoute(path);
    switch (screen) {
      case 'Home':
        setCurrentScreen(SCREENS.HOME);
        break;
      case 'IdeaValidator':
        setCurrentScreen(SCREENS.IDEA_VALIDATOR);
        break;
      case 'MarketInsights':
        setCurrentScreen(SCREENS.MARKET_INSIGHTS);
        break;
      case 'PitchDeckAnalyzer':
        setCurrentScreen(SCREENS.PITCH_DECK_ANALYZER);
        break;
      default:
        setCurrentScreen(SCREENS.HOME);
        break;
    }
  }, [location.pathname]);

  useEffect(() => {
    const handlePopState = () => {
      const path = location.pathname;
      const screen = getScreenFromRoute(path);
      switch (screen) {
        case 'Home':
          setCurrentScreen(SCREENS.HOME);
          break;
        case 'IdeaValidator':
          setCurrentScreen(SCREENS.IDEA_VALIDATOR);
          break;
        case 'MarketInsights':
          setCurrentScreen(SCREENS.MARKET_INSIGHTS);
          break;
        case 'PitchDeckAnalyzer':
          setCurrentScreen(SCREENS.PITCH_DECK_ANALYZER);
          break;
        default:
          setCurrentScreen(SCREENS.HOME);
          break;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--sidebar-background-color",
      colors.componentBackground,
    );
  }, [colors.componentBackground]);
  const renderMainComponent = () => {
    const CurrentMainComponent = COMPONENTS?.[currentScreen] ?? Home;
    const CurrentMainComponentData = COMPONENT_PROPS?.[currentScreen];
    return <CurrentMainComponent data={CurrentMainComponentData} />;
  };

  return {
    colors,
    renderMainComponent,
    headerProps: COMPONENT_PROPS?.Header,
    sidebarProps: COMPONENT_PROPS?.Sidebar,
    footerProps: COMPONENT_PROPS?.Footer,
    isMenuOpened,
    currentScreen,
    changeScreen,
    navigateToRoute,
  };
};
